export type ChallengeStatus =
  | 'not_started'
  | 'active'
  | 'completed'
  | 'failed'
  | 'reward_delivered'

export type ChallengeType =
  | 'aggregation'
  | 'check_in'
  | 'focus_time'
  | 'plant_count'
  | 'event'
  | 'tree_variety'
  | 'purchase'
  | 'streak'

export type Challenge = {
  id: number
  kind: 'daily' | 'monthly' | 'new_user'
  title_key: string
  image_url: string
  parent_challenge_id: number
  children_challenges?: Challenge[]
}

export type ChallengeDifficulty = {
  id: number
  difficulty: number
  type: ChallengeType
  event_name: string
  tree_type: number | null
  reward_type_id: number | null
  deeplink?: string
  // Marks a Plus challenge: claiming its reward requires an active subscription.
  // Gates the claim, not visibility. Daily-only today. May be absent on older
  // responses, so always coerce with `=== true`.
  requires_subscription?: boolean
  challenge: Challenge
}

export type ChallengeProgress = {
  id: number
  user_id: number
  progress: number
  target: number
  status: ChallengeStatus
  available_at: string
  due_at: string
  completed_at: string
  reward_delivered_at: string
  metadata: number[]
  challenge_difficulty: ChallengeDifficulty
}

export type FocusChallengesResponse = {
  challenge_progresses: ChallengeProgress[]
}

export type CheckinSlotState =
  | 'pending'
  | 'available'
  | 'pressing'
  | 'collected'
  | 'waiting'

export type ChestState = 'locked' | 'ready' | 'claimed'

export type ChestLevel = 'lv1' | 'lv2' | 'lv3'

export type ClaimState = 'progress' | 'ready' | 'claimed'

export type DailyTask = {
  progress: ChallengeProgress
  chestState: ChestState
  chestLevel: ChestLevel
  requiresSubscription: boolean
}

export type CheckinSlot = {
  progress: ChallengeProgress | null
  state: CheckinSlotState
}

export type NormalizedFocusChallenges = {
  monthly: ChallengeProgress | null
  monthlyClaimState: ClaimState
  checkinSlots: [CheckinSlot, CheckinSlot, CheckinSlot]
  daily: DailyTask[]
  raw: ChallengeProgress[]
}

function classifyCheckin(p: ChallengeProgress | null): CheckinSlotState {
  if (!p) return 'pending'
  if (p.status === 'reward_delivered') return 'collected'
  if (p.status === 'completed') return 'available'
  if (
    (p.status === 'active' || p.status === 'not_started') &&
    new Date(p.available_at).getTime() > Date.now()
  )
    return 'waiting'
  return 'pending'
}

function classifyChest(p: ChallengeProgress): ChestState {
  if (p.status === 'reward_delivered') return 'claimed'
  if (p.status === 'completed') return 'ready'
  return 'locked'
}

function classifyMonthly(p: ChallengeProgress | null): ClaimState {
  if (!p) return 'progress'
  if (p.status === 'reward_delivered') return 'claimed'
  if (p.status === 'completed') return 'ready'
  return 'progress'
}

function chestLevelFor(
  p: ChallengeProgress,
  fallbackIndex: number,
): ChestLevel {
  // Provisional rule per spec — confirm with backend. Use difficulty if present (1, 2, 3 → lv1, lv2, lv3); fallback to fallbackIndex (0,1,2 → lv1,lv2,lv3).
  const d = p.challenge_difficulty.difficulty
  if (d === 1) return 'lv1'
  if (d === 2) return 'lv2'
  if (d === 3) return 'lv3'
  return (['lv1', 'lv2', 'lv3'] as const)[Math.min(fallbackIndex, 2)]
}

export function normalizeFocusChallenges(
  res: FocusChallengesResponse,
): NormalizedFocusChallenges {
  const raw = res.challenge_progresses ?? []
  const monthly =
    raw.find((p) => p.challenge_difficulty.type === 'aggregation') ?? null
  const checkinList = raw
    .filter((p) => p.challenge_difficulty.type === 'check_in')
    .sort(
      (a, b) =>
        new Date(a.available_at).getTime() - new Date(b.available_at).getTime(),
    )
  const byDifficulty = raw
    .filter(
      (p) =>
        p.challenge_difficulty.type !== 'aggregation' &&
        p.challenge_difficulty.type !== 'check_in',
    )
    .sort(
      (a, b) =>
        a.challenge_difficulty.difficulty - b.challenge_difficulty.difficulty,
    )
  // Plus (subscription bonus) challenges are exempt from the
  // one-card-per-difficulty rule — keep every Plus progress, pinned to the top.
  const plus = byDifficulty.filter(
    (p) => p.challenge_difficulty.requires_subscription === true,
  )
  // Regular daily challenges show one card per difficulty. The server can return
  // extra progresses for the same difficulty; keep the first (server order is
  // preserved by the stable difficulty sort above) and drop the rest.
  const seenDifficulty = new Set<number>()
  const regular = byDifficulty.filter((p) => {
    if (p.challenge_difficulty.requires_subscription === true) return false
    const d = p.challenge_difficulty.difficulty
    if (seenDifficulty.has(d)) return false
    seenDifficulty.add(d)
    return true
  })
  // Pin Plus challenges to the top; each group stays difficulty-ascending
  // because the partition preserves the difficulty sort order.
  const dailyList = [...plus, ...regular]

  const checkinSlots: [CheckinSlot, CheckinSlot, CheckinSlot] = [0, 1, 2].map(
    (i) => {
      const p = checkinList[i] ?? null
      return { progress: p, state: classifyCheckin(p) }
    },
  ) as [CheckinSlot, CheckinSlot, CheckinSlot]

  const daily: DailyTask[] = dailyList.map((p, i) => {
    const requiresSubscription =
      p.challenge_difficulty.requires_subscription === true
    return {
      progress: p,
      chestState: classifyChest(p),
      // Plus challenges always award the top-tier chest regardless of difficulty.
      chestLevel: requiresSubscription ? 'lv3' : chestLevelFor(p, i),
      requiresSubscription,
    }
  })

  return {
    monthly,
    monthlyClaimState: classifyMonthly(monthly),
    checkinSlots,
    daily,
    raw,
  }
}
